import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Search,
  Loader2,
  Server,
  Users,
  GitBranch,
  Shield,
  Activity,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Filter,
  Network,
  ArrowRight,
  FileText,
  BookOpen,
  X,
  GripVertical,
} from 'lucide-react';
import { useSemanticSearch } from '../hooks/useQuery';
import { useEntities } from '../hooks/useEntities';

const TYPE_ICONS = {
  SERVICE: Server,
  TEAM: Users,
  PATTERN: GitBranch,
  API: Activity,
  REGULATION: Shield,
};

const TYPE_COLORS = {
  SERVICE: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  TEAM: 'bg-green-500/20 text-green-400 border-green-500/30',
  PATTERN: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  API: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  REGULATION: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const ENTITY_TYPES = ['SERVICE', 'TEAM', 'PATTERN', 'API', 'REGULATION'];

function ScoreIndicator({ score }) {
  const percentage = Math.round(score * 100);
  let colorClass = 'bg-red-500';

  if (score >= 0.8) {
    colorClass = 'bg-green-500';
  } else if (score >= 0.6) {
    colorClass = 'bg-yellow-500';
  } else if (score >= 0.4) {
    colorClass = 'bg-orange-500';
  }

  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-2 bg-dark-600 rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClass} transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="text-xs text-dark-300 font-mono w-12">
        {score.toFixed(3)}
      </span>
    </div>
  );
}

function RelationshipConnection({ target, relationType, description }) {
  const Icon = TYPE_ICONS[target.entity_type] || Server;
  const colorClass = TYPE_COLORS[target.entity_type] || TYPE_COLORS.SERVICE;

  return (
    <div className="pl-8 border-l border-dark-500 py-3 flex items-start gap-3">
      <div className={`w-8 h-8 rounded flex items-center justify-center border flex-shrink-0 ${colorClass}`}>
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="text-sm font-semibold text-dark-100">{target.name}</h4>
          <span className="text-xs px-2 py-0.5 rounded bg-dark-700 text-dark-300">
            {target.entity_type}
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-dark-400">
          <span className="px-2 py-1 bg-dark-700 rounded-full">{relationType}</span>
          {description && <span>{description}</span>}
        </div>
      </div>
    </div>
  );
}

function SearchResultWithRelationships({ result, allEntities, relationships, onSelect, isExpanded, onToggleExpand, onOpenDocumentPanel }) {
  const Icon = TYPE_ICONS[result.entity_type] || Server;
  const colorClass = TYPE_COLORS[result.entity_type] || TYPE_COLORS.SERVICE;
  const [expandedData, setExpandedData] = useState(false);
  const [expandedRelationships, setExpandedRelationships] = useState({});

  // Get related entities for this result
  const outgoingRelations = relationships.filter(r => r.source_id === result.entity_id);
  const incomingRelations = relationships.filter(r => r.target_id === result.entity_id);

  // Helper function to get entity details
  const getEntityDetails = (entityId) => {
    return allEntities.find(e => e.entity_id === entityId || e.id === entityId);
  };

  const toggleRelationshipData = (key) => {
    setExpandedRelationships(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <div className="card-hover rounded-lg border border-dark-600 overflow-hidden animate-fadeIn">
      {/* Main Result */}
      <div className="p-4 bg-dark-700/20 border-b border-dark-600">
        <div className="flex items-start gap-4">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center border flex-shrink-0 ${colorClass}`}>
            <Icon size={20} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-accent-purple">
                {result.name}
              </h3>
              <span className={`text-xs px-2 py-0.5 rounded ${colorClass}`}>
                {result.entity_type}
              </span>
            </div>

            <p className="text-sm text-dark-300 line-clamp-2 mb-2">
              {result.description}
            </p>

            <ScoreIndicator score={result.score} />
          </div>

          <div className="flex gap-2 flex-shrink-0">
            {/* Open Full Document Button */}
            <button
              onClick={() => onOpenDocumentPanel({
                entityName: result.name,
                entityType: result.entity_type,
                fullContent: result.description + '\n\n' + (result.metadata ? Object.entries(result.metadata).map(([k, v]) => `${k}: ${v}`).join('\n') : ''),
                metadata: result.metadata
              })}
              className="p-2 hover:bg-accent-purple/20 rounded transition-colors text-accent-purple"
              title="View full document"
            >
              <ArrowRight size={18} />
            </button>

            {/* Data Panel Button */}
            <button
              onClick={() => setExpandedData(!expandedData)}
              className="p-2 hover:bg-dark-600 rounded transition-colors"
              title="Show document data"
            >
              {expandedData ? (
                <ChevronUp size={18} className="text-accent-purple" />
              ) : (
                <ChevronDown size={18} className="text-dark-400" />
              )}
            </button>

            {/* Expand Relationships Button */}
            <button
              onClick={() => onToggleExpand(result.entity_id)}
              className="p-2 hover:bg-dark-600 rounded transition-colors"
              title="Show relationships"
            >
              <ChevronRight
                size={18}
                className={`text-dark-400 transition-all ${isExpanded ? 'rotate-90 text-accent-purple' : ''}`}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Data Panel - Shows document source information */}
      {expandedData && (
        <div className="border-t border-dark-600 bg-dark-800/50 p-4 space-y-3 animate-fadeIn">
          <div className="flex items-center gap-2 mb-2">
            <FileText size={16} className="text-accent-purple" />
            <h4 className="text-xs font-semibold text-dark-300">Source Document Data</h4>
          </div>

          <div className="space-y-2">
            <div className="bg-dark-700/50 p-3 rounded border border-dark-600/50">
              <p className="text-xs text-dark-400 mb-1">Entity Name</p>
              <p className="text-sm font-mono text-dark-100">{result.name}</p>
            </div>

            <div className="bg-dark-700/50 p-3 rounded border border-dark-600/50">
              <p className="text-xs text-dark-400 mb-1">Type</p>
              <p className="text-sm font-mono text-dark-100">{result.entity_type}</p>
            </div>

            <div className="bg-dark-700/50 p-3 rounded border border-dark-600/50">
              <p className="text-xs text-dark-400 mb-1">Description</p>
              <p className="text-sm text-dark-200">{result.description}</p>
            </div>

            {result.metadata && Object.keys(result.metadata).length > 0 && (
              <div className="bg-dark-700/50 p-3 rounded border border-dark-600/50">
                <p className="text-xs text-dark-400 mb-2">Metadata</p>
                <div className="space-y-1">
                  {Object.entries(result.metadata).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-xs">
                      <span className="text-dark-400">{key}:</span>
                      <span className="text-dark-200 font-mono">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Related Entities - Only show when expanded */}
      {isExpanded && (outgoingRelations.length > 0 || incomingRelations.length > 0) && (
        <div className="border-t border-dark-600 bg-dark-800/50 p-4 animate-fadeIn space-y-3">
          <h4 className="text-xs font-semibold text-dark-300 flex items-center gap-2">
            <Network size={14} />
            Related Entities ({outgoingRelations.length + incomingRelations.length})
          </h4>

          <div className="space-y-3">
            {/* Outgoing relationships */}
            {outgoingRelations.map((rel, idx) => {
              const target = getEntityDetails(rel.target_id);
              if (!target) return null;
              const key = `out-${idx}`;
              const isDataExpanded = expandedRelationships[key];

              return (
                <div key={key} className="bg-dark-700/30 border border-dark-600/50 rounded-lg overflow-hidden">
                  <div className="p-3 flex items-start justify-between hover:bg-dark-600/20 transition-colors">
                    <div className="flex-1">
                      <RelationshipConnection
                        target={target}
                        relationType={rel.rel_type}
                        description={rel.description}
                      />
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => onOpenDocumentPanel({
                          entityName: target.name,
                          entityType: target.entity_type,
                          fullContent: target.description,
                          metadata: target.metadata
                        })}
                        className="p-1 hover:bg-accent-purple/20 rounded text-accent-purple"
                        title="View full document"
                      >
                        <ArrowRight size={14} />
                      </button>
                      <button
                        onClick={() => toggleRelationshipData(key)}
                        className="p-1 hover:bg-dark-600 rounded"
                        title="Show related entity data"
                      >
                        {isDataExpanded ? (
                          <ChevronUp size={16} className="text-accent-purple" />
                        ) : (
                          <ChevronDown size={16} className="text-dark-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {isDataExpanded && (
                    <div className="px-3 pb-3 border-t border-dark-600/50 space-y-2 bg-dark-800/50">
                      <div className="text-xs bg-dark-700/50 p-2 rounded border border-dark-600/30">
                        <p className="text-dark-400 mb-1">Name:</p>
                        <p className="font-mono text-dark-100">{target.name}</p>
                      </div>
                      <div className="text-xs bg-dark-700/50 p-2 rounded border border-dark-600/30">
                        <p className="text-dark-400 mb-1">Type:</p>
                        <p className="font-mono text-dark-100">{target.entity_type}</p>
                      </div>
                      <div className="text-xs bg-dark-700/50 p-2 rounded border border-dark-600/30">
                        <p className="text-dark-400 mb-1">Description:</p>
                        <p className="text-dark-200 line-clamp-3">{target.description}</p>
                      </div>
                      <div className="text-xs bg-dark-700/50 p-2 rounded border border-dark-600/30">
                        <p className="text-dark-400 mb-1">Relationship:</p>
                        <p className="text-dark-200">{rel.description || 'No description provided'}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Incoming relationships */}
            {incomingRelations.map((rel, idx) => {
              const source = getEntityDetails(rel.source_id);
              if (!source) return null;
              const key = `in-${idx}`;
              const isDataExpanded = expandedRelationships[key];

              return (
                <div key={key} className="bg-dark-700/30 border border-dark-600/50 rounded-lg overflow-hidden">
                  <div className="p-3 flex items-start justify-between hover:bg-dark-600/20 transition-colors">
                    <div className="flex-1">
                      <RelationshipConnection
                        target={source}
                        relationType={`← ${rel.rel_type}`}
                        description={rel.description}
                      />
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => onOpenDocumentPanel({
                          entityName: source.name,
                          entityType: source.entity_type,
                          fullContent: source.description,
                          metadata: source.metadata
                        })}
                        className="p-1 hover:bg-accent-purple/20 rounded text-accent-purple"
                        title="View full document"
                      >
                        <ArrowRight size={14} />
                      </button>
                      <button
                        onClick={() => toggleRelationshipData(key)}
                        className="p-1 hover:bg-dark-600 rounded"
                        title="Show related entity data"
                      >
                        {isDataExpanded ? (
                          <ChevronUp size={16} className="text-accent-purple" />
                        ) : (
                          <ChevronDown size={16} className="text-dark-400" />
                        )}
                      </button>
                    </div>
                  </div>

                  {isDataExpanded && (
                    <div className="px-3 pb-3 border-t border-dark-600/50 space-y-2 bg-dark-800/50">
                      <div className="text-xs bg-dark-700/50 p-2 rounded border border-dark-600/30">
                        <p className="text-dark-400 mb-1">Name:</p>
                        <p className="font-mono text-dark-100">{source.name}</p>
                      </div>
                      <div className="text-xs bg-dark-700/50 p-2 rounded border border-dark-600/30">
                        <p className="text-dark-400 mb-1">Type:</p>
                        <p className="font-mono text-dark-100">{source.entity_type}</p>
                      </div>
                      <div className="text-xs bg-dark-700/50 p-2 rounded border border-dark-600/30">
                        <p className="text-dark-400 mb-1">Description:</p>
                        <p className="text-dark-200 line-clamp-3">{source.description}</p>
                      </div>
                      <div className="text-xs bg-dark-700/50 p-2 rounded border border-dark-600/30">
                        <p className="text-dark-400 mb-1">Relationship:</p>
                        <p className="text-dark-200">{rel.description || 'No description provided'}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function IntelligentSearch({ onSearch }) {
  const [query, setQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedResults, setExpandedResults] = useState(new Set());
  const [selectedDocumentPanel, setSelectedDocumentPanel] = useState(null);
  const [panelWidth, setPanelWidth] = useState(384); // Default w-96 = 384px
  const [isResizing, setIsResizing] = useState(false);
  const { results, loading, error, search, clear } = useSemanticSearch();
  const { entities, relationships } = useEntities();
  const panelRef = useRef(null);

  // Handle panel resize
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      const newWidth = window.innerWidth - e.clientX;
      // Clamp between 300px and 800px
      setPanelWidth(Math.min(Math.max(newWidth, 300), 800));
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  const toggleExpandResult = useCallback((entityId) => {
    setExpandedResults(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entityId)) {
        newSet.delete(entityId);
      } else {
        newSet.add(entityId);
      }
      return newSet;
    });
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (query.trim()) {
      search(query, { entityTypes: selectedTypes });
      onSearch?.(query);
    }
  }, [query, selectedTypes, search, onSearch]);

  const toggleType = (type) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const handleClear = () => {
    setQuery('');
    clear();
    setExpandedResults(new Set());
  };

  const suggestedQueries = [
    'authentication',
    'payment processing',
    'fraud detection',
    'compliance',
    'platform engineering',
    'data warehouse',
  ];


  return (
    <div className="h-full flex flex-col relative">
      {/* Document Panel - Slides out from right with resizable width */}
      {selectedDocumentPanel && (
        <div
          ref={panelRef}
          className="fixed right-0 top-0 h-full bg-dark-800 border-l border-dark-600 shadow-2xl z-50 flex flex-col animate-fadeIn"
          style={{ width: `${panelWidth}px` }}
        >
          {/* Resize Handle */}
          <div
            onMouseDown={handleMouseDown}
            className={`absolute left-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-accent-purple/30 transition-colors flex items-center justify-center group ${isResizing ? 'bg-accent-purple/30' : ''}`}
          >
            <div className="opacity-0 group-hover:opacity-100 transition-opacity">
              <GripVertical size={16} className="text-accent-purple" />
            </div>
          </div>

          {/* Panel Header */}
          <div className="p-4 border-b border-dark-600 flex items-center justify-between ml-2">
            <h3 className="font-semibold flex items-center gap-2">
              <FileText size={18} className="text-accent-purple" />
              Source Document
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-dark-400">{panelWidth}px</span>
              <button
                onClick={() => setSelectedDocumentPanel(null)}
                className="p-2 hover:bg-dark-700 rounded transition-colors"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Panel Content */}
          <div className="flex-1 overflow-auto p-4 space-y-4 ml-2">
            <div>
              <p className="text-xs text-dark-400 mb-1">Entity</p>
              <p className="text-sm font-semibold text-accent-purple">{selectedDocumentPanel.entityName}</p>
            </div>

            <div>
              <p className="text-xs text-dark-400 mb-1">Type</p>
              <p className="text-sm font-mono">{selectedDocumentPanel.entityType}</p>
            </div>

            <div className="bg-dark-700/50 p-3 rounded border border-dark-600/50">
              <p className="text-xs text-dark-400 mb-2">Full Document Content</p>
              <p className="text-sm leading-relaxed text-dark-200 whitespace-pre-wrap">
                {selectedDocumentPanel.fullContent}
              </p>
            </div>

            {selectedDocumentPanel.metadata && Object.keys(selectedDocumentPanel.metadata).length > 0 && (
              <div>
                <p className="text-xs text-dark-400 mb-2">Metadata</p>
                <div className="space-y-1 bg-dark-700/50 p-3 rounded border border-dark-600/50">
                  {Object.entries(selectedDocumentPanel.metadata).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-xs">
                      <span className="text-dark-400">{key}:</span>
                      <span className="font-mono text-dark-200">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className={`p-6 border-b border-dark-600 flex-shrink-0 ${selectedDocumentPanel ? 'opacity-50 pointer-events-none' : ''}`}>
        <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
          <Network size={28} />
          Intelligent Search
        </h1>
        <p className="text-dark-300">
          Search for services, teams, patterns, and APIs, then explore their relationships
        </p>
      </div>

      {/* Search Form */}
      <div className={`p-6 border-b border-dark-600 flex-shrink-0 ${selectedDocumentPanel ? 'opacity-50 pointer-events-none' : ''}`}>
        <form onSubmit={handleSubmit}>
          <div className="flex gap-3">
            <div className="relative flex-1">
              <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-dark-400" />
              <input
                type="text"
                placeholder="Search for any entity..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg pl-12 pr-4 py-3 text-lg focus:outline-none focus:border-accent-purple transition-colors"
              />
            </div>

            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`px-4 rounded-lg border transition-colors ${
                showFilters || selectedTypes.length > 0
                  ? 'bg-accent-purple border-accent-purple text-white'
                  : 'bg-dark-700 border-dark-600 hover:border-dark-500'
              }`}
            >
              <Filter size={20} />
            </button>

            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="btn-primary px-6 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <Search size={18} />
              )}
              Search
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="mt-4 p-4 bg-dark-700 rounded-lg">
              <h3 className="text-sm font-semibold text-dark-300 mb-2">Filter by Type</h3>
              <div className="flex flex-wrap gap-2">
                {ENTITY_TYPES.map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => toggleType(type)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      selectedTypes.includes(type)
                        ? 'bg-accent-purple text-white'
                        : 'bg-dark-600 text-dark-300 hover:bg-dark-500'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          )}
        </form>

        {/* Suggested Queries */}
        {!results.length && !loading && (
          <div className="mt-4">
            <p className="text-sm text-dark-400 mb-2">Try searching for:</p>
            <div className="flex flex-wrap gap-2">
              {suggestedQueries.map(suggestion => (
                <button
                  key={suggestion}
                  onClick={() => {
                    setQuery(suggestion);
                    search(suggestion, { entityTypes: selectedTypes });
                  }}
                  className="px-3 py-1 bg-dark-700 hover:bg-dark-600 rounded-full text-sm text-dark-300 hover:text-white transition-colors"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div className={`flex-1 overflow-auto p-6 ${selectedDocumentPanel ? 'opacity-50 pointer-events-none' : ''}`}>
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 mb-4">
            {error}
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 size={40} className="animate-spin text-accent-purple mb-4" />
            <p className="text-dark-300">Searching across {results.length || 'all'} entities...</p>
          </div>
        )}

        {!loading && results.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-dark-400">
                Found <span className="font-semibold text-dark-200">{results.length}</span> results for "<span className="font-semibold text-accent-purple">{query}</span>"
              </p>
              <button
                onClick={handleClear}
                className="text-sm text-accent-purple hover:text-accent-purple-light transition-colors"
              >
                Clear Results
              </button>
            </div>

            <div className="space-y-4">
              {results.map((result, index) => (
                <SearchResultWithRelationships
                  key={result.entity_id || index}
                  result={result}
                  allEntities={entities}
                  relationships={relationships}
                  isExpanded={expandedResults.has(result.entity_id)}
                  onToggleExpand={toggleExpandResult}
                  onOpenDocumentPanel={setSelectedDocumentPanel}
                />
              ))}
            </div>
          </div>
        )}

        {!loading && !results.length && query && (
          <div className="flex flex-col items-center justify-center py-12 text-dark-400">
            <Search size={40} className="mb-4 opacity-50" />
            <p>No results found for "{query}"</p>
            <p className="text-sm mt-2">Try different keywords or adjust filters</p>
          </div>
        )}

        {!loading && !results.length && !query && (
          <div className="flex flex-col items-center justify-center py-12 text-dark-400">
            <Search size={40} className="mb-4 opacity-50" />
            <p>Enter a search query to find entities and explore relationships</p>
            <p className="text-sm mt-2">
              Discover how services, teams, and patterns are connected
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default IntelligentSearch;
